import { useEffect, useState, useRef } from "react";
import {
  JOURS_TRAVAILLES_PAR_MOIS,
  PAY_BRACKETS,
  computeTimeCostFromTasks,
  enJournees,
  recuperablesParSemaine,
} from "@riseafrica/scoring";
import { CONFIG } from "../config";
import { FIRST_STEP } from "../content/copy";
import { fetchPlanActions } from "../lib/api";
import type { EchecPlan, PlanActions as Plan } from "../lib/api";
import { dessinerCarte, enregistrerCarte, nomCarte, partagerImage } from "../lib/carte";
import { chipClass, fmt, libelleNiveau } from "../lib/format";
import {
  cheminDeParrainage,
  copyTestLink,
  downloadResult,
  lienDeParrainage,
  shareWhatsApp,
} from "../lib/share";
import { track } from "../lib/track";
import { useApp } from "../state/AppState";
import type { Volet1 } from "../state/AppState";
import { Fil } from "../components/Fil";
import { RappelAgenda } from "../components/RappelAgenda";
import { PlanActions, PlanEchoue } from "../components/PlanActions";
import { BoutonAchat } from "../components/BoutonAchat";
import { ApercuRapport } from "../components/ApercuRapport";

/**
 * Tranche médiane, celle que le volet 2 propose par défaut. Elle sert à donner
 * un ordre de grandeur **avant** le volet 2 — le montant en francs est la seule
 * chose qui saisit vraiment, et il était jusqu'ici derrière un second
 * formulaire. L'hypothèse est affichée en toutes lettres : sans elle, ce serait
 * un chiffre présenté comme le sien, donc un faux signal.
 */
const TRANCHE_BASSE = PAY_BRACKETS[0];
const TRANCHE_HAUTE = PAY_BRACKETS[PAY_BRACKETS.length - 1];

/** Arrondi grossier : un ordre de grandeur ne se donne pas au franc près. */
function ordreDeGrandeur(montant: number): number {
  const pas = montant >= 1000000 ? 100000 : 50000;
  return Math.max(pas, Math.round(montant / pas) * pas);
}


/** Volet 01 — restitution de l'indice d'exposition. */
export function Result1({ t1 }: { t1: Volet1 }) {
  const {
    goTo,
    say,
    registerShare,
    parrainage,
    partages,
    rafraichirParrainage,
    aSonCompte,
    setTitresPlan,
    dirigeantActif,
    reportPrice,
  } = useApp();
  const [message, setMessage] = useState("");
  const [copie, setCopie] = useState("");

  /*
    Trois états, et non deux — 26/08/2026.

    Le bouton d'enregistrement n'apparaissait que `carte !== null`, c'est-à-dire
    après le dessin : jusqu'à 1 200 ms d'attente des polices, puis le tracé et
    l'encodage. Mesuré au navigateur, il surgissait environ trois secondes après
    l'écran — absent, donc, au premier regard.

    Or c'est le seul levier d'acquisition organique du produit, et l'acquisition
    est sa famille la plus faible : l'achat payant ne ferme pas à 3 000 F. Un
    bouton qui arrive après que l'œil est passé ne sert à personne.

    « En cours » est distinct de « échouée ». Le bouton s'affiche pendant le
    dessin et le clic attend la fin ; il ne s'affiche pas si le dessin a
    échoué — proposer d'enregistrer une image qui n'existera jamais serait pire
    que de ne rien proposer.
  */
  const [carte, setCarte] = useState<Blob | null | "encours">("encours");
  const [plan, setPlan] = useState<Plan | null>(null);
  const [planEnCours, setPlanEnCours] = useState(true);
  const [echecPlan, setEchecPlan] = useState<EchecPlan | null>(null);
  // Change à chaque « Réessayer » : c'est ce qui relance la demande.
  const [essaiPlan, setEssaiPlan] = useState(0);

  const { exposure, taches, fromCv, metier } = t1;
  const { score, gradient, comparaison, repere, groupeLibelle, high, total } = exposure;
  const premiereAction = FIRST_STEP[exposure.groupe] ?? FIRST_STEP.ADMIN;

  // Ordre de grandeur du coût annuel, quand les heures ont été déclarées.
  const heuresDeclarees = taches.some((tache) => (tache.heures ?? 0) > 0);
  const cout = (remuneration: number) =>
    computeTimeCostFromTasks(
      taches.map((tache) => ({
        texte: tache.texte,
        niveau: tache.niveau,
        heures: tache.heures ?? 0,
      })),
      remuneration,
    );
  /*
    Le temps d'abord, l'argent ensuite.

    Cet encadré annonçait « 600 000 à 7 700 000 F par an » : une fourchette de
    un à treize, en gros caractères, avant toute autre chose. Elle est honnête —
    la rémunération est encore inconnue — mais elle ne dit rien à personne, et
    elle occupait la place du seul chiffre déjà certain à ce stade : le temps.

    Les heures, elles, ne dépendent d'aucune hypothèse. On les met devant, en
    journées de travail, et la fourchette passe en explication.
  */
  /*
    Le chiffre au moment du choc — 28/09/2026.

    L'indice dit **combien** on est exposé ; il ne dit pas ce que la personne y
    gagne. Ce chiffre-là existait, mais trois blocs plus bas et en coût, pas en
    gain : on l'atteignait une fois l'émotion retombée. On le pose donc sous
    l'indice, en heures par semaine, parce qu'il ne dépend d'aucune hypothèse —
    le barème publié appliqué aux heures que la personne vient de déclarer.

    C'est **le même chiffre** que la couverture du rapport et que la carte du
    temps récupéré : `recuperablesParSemaine`, et rien d'autre. Un gain annoncé
    ici et un autre imprimé là serait un faux signal sur le chiffre qui vend.
  */
  const recuperables = heuresDeclarees
    ? recuperablesParSemaine(taches.map((tache) => ({ niveau: tache.niveau, heures: tache.heures ?? 0 })))
    : 0;
  const estimation = heuresDeclarees
    ? {
        heures: Math.round(cout(TRANCHE_BASSE.valeur).hours),
        journees: enJournees(cout(TRANCHE_BASSE.valeur).hours),
        bas: ordreDeGrandeur(cout(TRANCHE_BASSE.valeur).money),
        haut: ordreDeGrandeur(cout(TRANCHE_HAUTE.valeur).money),
      }
    : null;

  useEffect(() => {
    say(`Votre indice est de ${score} sur 100 — ${gradient.libelle}`);
  }, [say, score, gradient.libelle]);

  /*
    Le plan part dès l'affichage du score, en arrière-plan. Rien n'est bloqué :
    l'indice, les tâches et l'ordre de grandeur sont déjà à l'écran pendant que
    la première action s'écrit.
  */
  useEffect(() => {
    let vivant = true;
    setPlanEnCours(true);
    setEchecPlan(null);
    void fetchPlanActions(metier, taches).then((resultat) => {
      if (!vivant) return;
      if (resultat.statut === "ok") {
        setPlan(resultat.plan);
        // Ce qui est montré ici sera exigé du rapport — 06/09/2026.
        setTitresPlan([resultat.plan.action.titre, ...resultat.plan.verrouillees]);
      } else {
        setTitresPlan(null);
        // Compté, donc visible dans les mesures : un plan qui n'arrive pas est
        // un achat qui ne se fera pas, et il ne doit pas disparaître en silence.
        setEchecPlan(resultat.raison);
        track("plan_indisponible", { raison: resultat.raison, metier });
      }
      setPlanEnCours(false);
    });
    return () => {
      vivant = false;
    };
    // `taches` est figé pour un résultat donné : le recalcul n'apporterait rien.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metier, essaiPlan]);

  /*
    La carte est dessinée dès l'arrivée sur l'écran, et non au clic : sur
    plusieurs navigateurs, un partage natif déclenché après une attente n'est
    plus rattaché au geste de l'utilisateur et échoue sans rien dire.
  */
  const dessin = useRef<Promise<Blob | null> | null>(null);
  useEffect(() => {
    let vivant = true;
    const promesse = dessinerCarte({
      score,
      gradient: gradient.libelle,
      metier,
      high,
      total,
      hote: window.location.host,
      chemin: cheminDeParrainage(),
    });
    dessin.current = promesse;
    void promesse.then((blob) => {
      if (vivant) setCarte(blob);
    });
    return () => {
      vivant = false;
    };
  }, [score, gradient.libelle, metier, high, total]);

  /** La carte, qu'elle soit déjà prête ou encore au pinceau. */
  const carteQuandPrete = async (): Promise<Blob | null> =>
    carte === "encours" ? ((await dessin.current) ?? null) : carte;

  // Un filleul a pu compléter son diagnostic entre-temps : on redemande l'état
  // à l'arrivée sur l'écran, une seule fois.
  useEffect(() => {
    rafraichirParrainage();
  }, [rafraichirParrainage]);

  /**
   * Ce que l'on peut dire sans mesure : ce que l'utilisateur a fait lui-même.
   * Le décompte des filleuls vient du serveur ou n'est pas affiché.
   */
  const compteParrainage =
    parrainage && parrainage.filleuls > 0
      ? `${parrainage.filleuls} sur ${parrainage.seuil} — comptées sur les diagnostics réellement complétés.`
      : partages > 0
        ? `Vous avez partagé le vôtre ${partages === 1 ? "une fois" : `${partages} fois`}. Le décompte se fait sur les diagnostics complétés, pas sur les partages.`
        : "";

  // Le message partagé n'avance que des faits : l'indice, son niveau, et le
  // décompte des tâches. Aucun rang, aucune comparaison non mesurée.
  const messagePartage = `Mon indice d'exposition à l'IA : ${score}/100 (${gradient.libelle}). ${high} de mes ${total} tâches principales sont déjà automatisables. Testez votre métier :`;

  /*
    L'image d'abord, le texte en repli. Sur WhatsApp, ce qui circule en statut
    et dans les groupes est une image ; un lien seul se lit et s'oublie. Le
    partage natif est le seul chemin qui dépose vraiment le fichier dans une
    conversation, et tous les appareils ne le proposent pas.
  */
  const partager = async () => {
    registerShare();
    if (carte !== null && carte !== "encours") {
      /*
        Le lien de parrainage, et non l'adresse nue du site.

        Ce chemin — l'image — est celui que le commentaire ci-dessus désigne
        comme le seul qui circule vraiment dans une conversation. Il envoyait
        `window.location.origin`, donc un lien **sans identifiant de parrain** :
        aucune arrivée venue d'un partage par image ne pouvait être attribuée.

        La croissance du produit ne tient qu'au partage — l'acquisition payante
        ne ferme pas à 3 000 F. Le taux de parrainage est donc l'indicateur à
        suivre avant tous les autres, et il était sous-compté sur sa voie
        principale, sans que rien ne le signale : le compteur montait un peu,
        grâce au repli texte, et personne n'avait de raison de le croire faux.
      */
      const envoye = await partagerImage(
        carte,
        `${messagePartage} ${lienDeParrainage()}`,
        nomCarte(score),
      );
      if (envoye) {
        track("share_image", { score });
        return;
      }
    }
    shareWhatsApp(messagePartage);
  };

  return (
    <section>
      <div className="shell narrow app">
        <Fil etape={1} fait />
        <div className="verdict">
          <div className="kicker">Indice d'exposition — {metier}</div>
          <div className="score">
            {score}
            <span>/100</span>
          </div>
          {/*
            Aucune comparaison n'est affichée avant d'avoir la population qui
            la justifie. Un rang dérivé du score serait une affirmation sur des
            profils qui n'existent pas (règle non négociable n° 6).
          */}
          <div className="rank">
            {comparaison.disponible
              ? `Un indice plus élevé que celui de ${comparaison.percentile} % des ${comparaison.effectif} profils analysés en « ${groupeLibelle.toLowerCase()} »`
              : `Sur les ${repere.professions} métiers de « ${groupeLibelle.toLowerCase()} » notés par l'Organisation internationale du Travail (OIT), ${repere.endessous} ont un indice plus bas que le vôtre ; ramenés sur l'échelle de votre indice, ils vont de ${repere.min} à ${repere.max}`}
          </div>
          {/*
            Le repère de l'OIT tient la place tant que la comparaison entre
            confrères n'a pas ses 200 diagnostics — mais il ne prend pas son
            nom. On dénombre ici des **professions recensées**, là-bas des
            **profils analysés** : deux populations, deux phrases, et la
            seconde est annoncée pour qu'on sache qu'elle vient.

            Sans cette ligne, le premier visiteur — celui dont dépend tout
            l'amorçage, puisque le partage est le seul canal — recevait un
            score sans aucun point de comparaison. C'est-à-dire une opinion là
            où le produit vend une mesure.
          */}
          {!comparaison.disponible && (
            <div className="repere">
              Repère tiré de l'indice de l'OIT, faute d'assez de diagnostics : la comparaison
              entre confrères est publiée dès {comparaison.seuil} diagnostics dans ce groupe.
            </div>
          )}
          {/*
            Le même effectif, dit autrement.

            La ligne au-dessus compare ; celle-ci situe. « Un indice plus élevé que celui de 68 % »
            parle d'un rang, « vous rejoignez 412 personnes » parle d'une
            appartenance — et c'est cette seconde phrase qui donne envie de
            partager, parce qu'elle dit qu'on n'est pas seul à se poser la
            question. Aucun chiffre nouveau n'est inventé : c'est l'effectif
            déjà affiché, dans une phrase qui lui donne son sens.
          */}
          {comparaison.disponible && comparaison.effectif > 0 && (
            <div className="rejoint">
              Vous rejoignez {fmt(comparaison.effectif)} personnes déjà mesurées dans votre groupe.
            </div>
          )}
          <div className="bars">
            {[1, 2, 3, 4].map((n) => (
              <i
                key={n}
                className={n < gradient.niveau ? "on" : n === gradient.niveau ? "peak" : ""}
              />
            ))}
          </div>
          <div className="bars-key">
            <span>Faible</span>
            <span>Modérée</span>
            <span>Forte</span>
            <span>Critique</span>
          </div>
          {/*
            Le mot, à l'endroit exact où on le rencontre — 26/08/2026.

            « Exposition » a un sens précis ici, et ce n'est pas celui de
            l'usage courant : la part de vos tâches qu'une IA réalise **déjà**,
            pondérée par le temps que vous y passez. Pas une probabilité de
            perdre son emploi. Le produit employait ce mot sur l'écran qui
            porte le chiffre, dans le rapport et sur l'attestation, sans l'avoir
            jamais défini nulle part.

            Le lien est discret et il ne concurrence rien : quelqu'un qui a
            compris passe outre, quelqu'un qui doute a une porte au lieu d'un
            doute.
          */}
          <p className="fine" style={{ margin: "10px 0 0" }}>
            <a
              className="lien-cible"
              href="/methode#mots"
              onClick={() => track("cta_click", { from: "resultat-lexique" })}
            >
              Ce que « exposition » veut dire ici
            </a>
          </p>
          {/*
            Les deux faces de l'indice, pour qui travaille à son compte —
            25/08/2026.

            **L'indice ne change pas.** Ni sa valeur, ni son barème, ni son
            libellé : deux échelles selon le statut rendraient le baromètre
            incomparable à lui-même, et c'est la seule chose qui rende ce
            produit citable. Ce qui s'ajoute est une lecture, pas une mesure.

            Elle est vraie des deux côtés, et c'est ce qui l'autorise. Un indice
            élevé dit que les clients de cette personne peuvent désormais faire
            avec une IA ce qu'ils lui achetaient — c'est la menace, et le
            salarié n'entend qu'elle. Il dit aussi qu'elle peut faire en une
            heure ce qui lui prenait la journée — c'est le levier. Un seul
            chiffre, deux conséquences, aucune inventée.

            Un salarié n'a pas cette seconde lecture au même degré : le temps
            qu'il récupère revient à son employeur avant de lui revenir. Un
            indépendant le facture ou le garde.
          */}
          {aSonCompte && (
            <div className="deuxfaces">
              <b>À votre compte, ce chiffre se lit deux fois.</b>
              <p>
                Vos clients peuvent désormais faire avec une IA une partie de ce qu'ils vous
                achètent — c'est ce que l'indice mesure. Mais vous pouvez produire en une heure ce
                qui vous en prenait quatre, et ce temps-là, personne ne vous le reprend : vous le
                facturez, ou vous le gardez.
              </p>
              <p>
                Le rapport traite les deux : ce qui est menacé dans votre offre, et ce que vous
                pouvez systématiser pour que votre activité tienne sans y passer vos soirées.
              </p>
            </div>
          )}
          <div className="gradient-label">{gradient.libelle}</div>
          <p>{gradient.texte}</p>
          {recuperables > 0 && (
            <div className="recuperable">
              <b>{fmt(recuperables)} h par semaine</b> récupérables sur les tâches que vous venez de
              déclarer, selon le barème publié. Le rapport dit comment les reprendre, tâche par
              tâche.
            </div>
          )}
        </div>

        {t1.degraded && (
          <div className="warn">
            Résultat approximatif : ce résultat s'appuie sur des tâches courantes d'un métier voisin
            du vôtre, et non sur votre métier. Relancez le diagnostic pour un résultat propre à votre
            métier.
          </div>
        )}

        {/*
          L'offre, sous l'indice et non huit écrans plus bas — 10/10/2026.

          Le seul bouton payant était au pied d'une page entière de gratuit : six
          personnes sur sept qui voyaient leur indice ouvraient le paiement, mais
          seulement après avoir déroulé tout ce que l'annonce promettait. Le
          rapport arrivait comme un second produit, après la satisfaction. Une
          phrase et un bouton, au moment où la question « et maintenant ? » se
          pose. Rien d'autre ne change : le bouton du bas reste.
        */}
        <div
          className="offre-courte"
          onClickCapture={() => track("cta_click", { from: "offre-sous-indice" })}
        >
          <div className="k">Le rapport complet · {fmt(reportPrice)} F</div>
          <p>
            Dix actions classées par heures rendues, sur vos tâches à vous : pour chacune, les
            étapes et le texte à copier dans l'assistant. Remboursé sans discussion sous quatorze
            jours.
          </p>
          <ApercuRapport />
          <BoutonAchat plan="report" libelle={`Obtenir mon rapport — ${fmt(reportPrice)} F`} />
        </div>

        {/*
          Le montant en francs est la seule chose qui saisit vraiment, et il
          était intégralement derrière le volet 2. Les heures par tâche sont
          pourtant déjà connues ici : il ne manquait que la tranche de
          rémunération. On donne donc un ordre de grandeur en annonçant
          l'hypothèse, et on renvoie au volet 2 pour le chiffre de la personne.
        */}
        {estimation !== null && (
          <div className="estimation">
            <div className="k">Ce que ces tâches vous prennent</div>
            <b>
              {estimation.journees > 0
                ? `environ ${estimation.journees} journées de huit heures par mois, sur ${JOURS_TRAVAILLES_PAR_MOIS} travaillées`
                : `environ ${estimation.heures} heures par mois`}
            </b>
            <p>
              Sur les heures que vous venez de déclarer, et sur elles seules. En argent, ce temps
              vaut de {fmt(estimation.bas)} à {fmt(estimation.haut)} F par an — l'écart est large
              parce que votre rémunération est la seule inconnue qui reste. Le volet 2 vous la
              demande et resserre le montant.
            </p>
            <p className="fine">
              Ce montant est ce que ce temps coûte, et non ce qu'il vous rapporterait.{" "}
              {aSonCompte
                ? "À votre compte, c'est vous qui le payez — en heures non facturées."
                : "Salarié, c'est votre employeur qui le paie."}
            </p>
          </div>
        )}

        {/* Un h2 et non un h3 : ce bloc n'est subordonné à aucun titre. Écrit
            en h3, il précédait le premier h2 de l'écran — un sous-titre avant
            son titre, pour qui lit la page au lecteur d'écran. */}
        <div className="firststep">
          <div className="k">Si vous ne faites qu'une chose</div>
          <h2>{premiereAction[0]}</h2>
          <p>{premiereAction[1]}</p>
        </div>

        {/*
          Relecture du 06/09/2026. Ce bloc s'affichait à tout indice sous 62,
          indépendants compris, promettait « la lecture par poste » — que le
          rapport à 3 000 F ne contient pas — et son bouton faisait défiler
          jusqu'à la carte de partage. Il ne s'adresse plus qu'à qui dirige, et
          il renvoie vers ce qui mesure vraiment une équipe : le diagnostic
          dirigeant, sur sa propre page.
        */}
        {exposure.groupe === "DIRECTION" && !aSonCompte && dirigeantActif && (
          <div className="manager">
            <div className="k">Si vous encadrez une équipe</div>
            <h3>Votre poste est peu exposé. Celui des personnes que vous encadrez l'est davantage.</h3>
            <p>
              Les postes administratifs et de back-office se situent souvent vingt à trente points
              au-dessus du vôtre. Le diagnostic dirigeant, sur une autre page, chiffre ce temps
              poste par poste — en heures, en personnes à temps plein et en francs.
            </p>
            <a className="btn btn-ghost" href="/dirigeant">
              Mesurer l'exposition de mon équipe
            </a>
          </div>
        )}

        <div className="block">
          <h2>Vos tâches, une par une</h2>
          <p className="sub">
            {fromCv
              ? "Extraites de votre CV, classées par niveau d'exposition."
              : "Les tâches que vous avez cochées, classées par niveau d'exposition."}
          </p>
          <div>
            {taches.map((tache, i) => (
              <div className="row" key={`${tache.texte}-${i}`}>
                <div className="task">
                  {tache.texte}
                  <span className="src">{fromCv ? "Votre CV" : "Vos réponses"}</span>
                </div>
                <i className={chipClass(tache.niveau)}>{libelleNiveau(tache.niveau)}</i>
              </div>
            ))}
          </div>
          <div className="tally">
            <b>
              {high} de vos {total} tâches principales
            </b>{" "}
            sont déjà réalisables par un outil disponible aujourd'hui en Afrique francophone, sans
            développement spécifique.
          </div>
        </div>

        {/*
          Le plan en dix actions. La première est offerte en entier et arrive
          d'un second appel, après l'affichage du score : le diagnostic occupe
          déjà une vingtaine des soixante secondes que tolère la réécriture
          hosting.

          Trois états, et aucun silencieux. En attente, on le dit. Abouti, on
          l'affiche. Échoué, on nomme la cause et on propose de réessayer —
          l'écran retombait auparavant sur deux actions courtes et des
          intitulés verrouillés, si bien que la personne voyait une version
          dégradée du produit en croyant voir le produit.
        */}
        {planEnCours || plan !== null ? (
          <PlanActions plan={plan} chargement={planEnCours} />
        ) : (
          <PlanEchoue
            raison={echecPlan ?? "inattendu"}
            onReessayer={() => setEssaiPlan((n) => n + 1)}
          />
        )}

        {/*
          Le passage au volet suivant vient **après** le plan d'actions.

          Placé avant, il invitait à quitter l'écran cent quatre-vingts pixels
          avant la seule démonstration gratuite du produit : on proposait de
          partir juste avant de prouver. Deux appels de même poids se
          disputaient l'écran, séparés par mille sept cents pixels.
        */}
        {/*
          Un seul appel de premier rang par écran, et c'est celui-ci.

          L'achat le précède en bouton contour. Ce n'est pas une concession au
          gratuit : c'est le volet qui mène à l'attestation, l'attestation est
          ce qui se partage, et le partage est le seul canal d'acquisition du
          produit — l'acquisition payante ne ferme pas à 3 000 F. On vend aussi
          mieux ensuite, puisque sans le volet 3 les dix actions sont écrites
          sans savoir si la personne débute ou maîtrise déjà.

          Ce qui précède ce commentaire a longtemps dit l'inverse — deux
          commentaires voisins se contredisaient, et le code suivait le second.
        */}
        <button type="button" className="btn btn-brass" onClick={() => goTo("s-t2")}>
          Volet suivant : ce que ça vous coûte →
        </button>
        <p className="hint">
          Une minute, et l'ordre de grandeur plus haut devient votre montant. Les trois volets
          faits, vous repartez avec votre attestation de niveau, à votre nom.
        </p>


        <div className="block">
          <h2>Comparez-vous à ceux qui font votre métier</h2>
          {/*
            Ce bloc ne parle jamais de « personnes ayant passé le test » tant
            que le serveur n'en a pas compté. Un partage émis dit ce que vous
            avez fait ; il ne dit rien de ce qu'il a provoqué.
          */}
          {/*
            Ce bloc promettait « votre second diagnostic est offert » en
            récompense de deux filleuls. Or le diagnostic est librement
            répétable, par tout le monde et sans condition : la récompense
            n'enlevait rien à personne, mais elle n'ajoutait rien non plus. Un
            signal creux au sens de la règle n° 6, placé exactement là où l'on
            demande à quelqu'un de partager.

            Ce qui reste dit ce qui est vrai, et c'est plus fort : un indice ne
            vaut que comparé, la comparaison entre confrères n'existe qu'à partir
            d'un certain nombre de réponses, et c'est le partage qui l'amène.
          */}
          <div className="refer">
            <b>
              {parrainage && parrainage.filleuls > 0
                ? `${parrainage.filleuls} personne${parrainage.filleuls > 1 ? "s ont" : " a"} passé le test depuis votre lien`
                : "Un indice ne vaut vraiment que comparé"}
            </b>
            {parrainage && parrainage.filleuls > 0
              ? "La comparaison entre confrères se publie dès que votre famille professionnelle compte assez de réponses. Chaque personne que vous amenez l'en rapproche."
              : "La comparaison entre confrères n'existe que si des confrères passent le test — et votre indice, lui, ne vaut que trois mois. Le prix du rapport ne bouge pas : il est le même pour tout le monde."}
            <span className="fine" style={{ display: "block", marginTop: "6px" }}>
              {compteParrainage}
            </span>
          </div>
          <div className="sharecard">
            <div className="small">Mon indice d'exposition</div>
            <div className="big">
              {score}/100 — {gradient.libelle}
            </div>
            {/* Sur la carte de partage, on n'avance que des faits vérifiables. */}
            <div className="small">
              {metier} · {high} de {total} tâches déjà automatisables
            </div>
          </div>
          <div className="share-opts">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={async () => {
                const ok = await copyTestLink();
                setCopie(ok ? "Lien copié — votre score n'y figure pas" : "Copie impossible sur ce navigateur");
                track("copy_link", { score });
              }}
            >
              Copier le lien seulement
            </button>
            <button type="button" className="btn btn-wa" onClick={() => void partager()}>
              Envoyer à une personne
            </button>
          </div>
          {carte !== null && (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ marginTop: "9px" }}
              onClick={() => {
                void carteQuandPrete().then((blob) => {
                  if (blob === null) {
                    setCopie("Image indisponible sur ce navigateur");
                    return;
                  }
                  enregistrerCarte(blob, score);
                  track("carte_enregistree", { score });
                  setCopie("Image enregistrée — prête pour votre statut WhatsApp");
                });
              }}
            >
              Enregistrer l'image de mon indice
            </button>
          )}
          <div className="copied" role="status" aria-live="polite">
            {copie}
          </div>
          <p className="fine" style={{ marginTop: "6px" }}>
            Votre indice n'est jamais publié ni visible par votre entreprise ou vos clients. Vous choisissez à qui
            vous l'envoyez — ou vous ne partagez que le lien du test, sans votre résultat.
          </p>
        </div>

        <div className="capture" id="capture-r1">
          <h3>Gardez votre résultat</h3>
          {/*
            Ce bloc demandait un numéro de téléphone et n'en faisait rien.

            Il était masqué tant que la messagerie est coupée, ce qui l'a rendu
            inoffensif jusqu'ici — et invisible à la relecture. Mais la garde
            portait sur le mauvais évènement : le jour où le compte WhatsApp
            Business s'ouvre, `CONFIG.messagerieActive` passe à vrai, ce
            formulaire réapparaît, et il continue de ne rien envoyer. Son seul
            effet était un compteur ; sa seule réponse, « envoi à brancher en
            production » — une phrase écrite pour nous et lue par des visiteurs.

            Un piège armé par l'interrupteur qu'on prévoit d'actionner n'est pas
            un défaut mineur : c'est celui qui se déclenche le jour du
            lancement. Le bloc part entièrement. Le jour où un envoi existe
            vraiment, il se réécrira avec le consentement, la conservation et la
            désinscription qu'il exige — c'est-à-dire pas en trois lignes.
          */}
          <p className="sub">
            Téléchargez-le, ou envoyez-le-vous par le partage ci-dessous : vous le retrouverez
            quand vous voudrez agir.
          </p>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              downloadResult({
                metier,
                score,
                gradient: gradient.libelle,
                taches,
                premiereAction,
              });
              setMessage("Résultat téléchargé");
            }}
          >
            Télécharger mon résultat
          </button>
          <div className="copied" role="status" aria-live="polite">
            {message}
          </div>
        </div>

        {/*
          Le rappel d'agenda vit maintenant dans son composant : il était ici et
          nulle part ailleurs, alors que trois autres écrans de résultat en
          avaient autant besoin. Voir `components/RappelAgenda.tsx`.
        */}
        <RappelAgenda
          proeminent
          titre="Refaire mon diagnostic d'exposition à l'IA"
          mesure={`Indice précédent : ${score}/100 (${gradient.libelle}).`}
          pourquoi="Votre indice dépend de la façon dont vous occupez votre semaine. Dès que vous confiez une tâche à une IA, cette répartition change — et votre indice avec elle."
          accroche="Votre indice vaut trois mois"
          ecran="r1"
        />

        {CONFIG.demoData && <PanneauTechnique t1={t1} />}

        <button type="button" className="back" onClick={() => goTo("home")}>
          ← Revenir au diagnostic
        </button>
      </div>
    </section>
  );
}

/**
 * Panneau technique. Il rend visible que le score est calculé par le code et non
 * par le modèle. Il n'est monté que si `demoData` est actif : il n'a pas sa
 * place en production.
 */
function PanneauTechnique({ t1 }: { t1: Volet1 }) {
  const { exposure, meta } = t1;
  return (
    <details className="tech">
      <summary>Panneau technique — à retirer en production</summary>
      <div className="body">
        <div className="kv">
          <span>Mode</span>
          <b>{meta.mode}</b>
        </div>
        <div className="kv">
          <span>Groupe professionnel</span>
          <b>
            {exposure.groupe} — {exposure.groupeLibelle}
          </b>
        </div>
        <div className="kv">
          <span>Base du groupe</span>
          <b>{exposure.base}</b>
        </div>
        <div className="kv">
          <span>Mix de tâches</span>
          <b>{Math.round(exposure.mix)}</b>
        </div>
        <div className="kv">
          <span>Score final (code, pas modèle)</span>
          <b>
            {exposure.score} = {exposure.base}×0,55 + {Math.round(exposure.mix)}×0,45
          </b>
        </div>
        <div className="kv">
          <span>Servi par le cache</span>
          <b className={meta.cacheHit ? "cache-hit" : undefined}>{meta.cacheHit ? "oui" : "non"}</b>
        </div>
        <div className="kv">
          <span>Version du barème</span>
          <b>{exposure.scoringVersion}</b>
        </div>
        <p style={{ margin: "14px 0 0" }}>
          Le modèle extrait et classe les tâches et choisit un groupe dans une liste fermée ; le
          score est calculé par le code. En mode métier le pack est mis en cache côté serveur :
          refaites le même métier pour voir l'appel modèle disparaître.
        </p>
      </div>
    </details>
  );
}
